import React, { useState } from "react";

const PrivacyPolicy = () => {
  const [isAgreed, setIsAgreed] = useState(false);

  const handleCheckboxChange = (e) => {
    setIsAgreed(e.target.checked);
  };

  return (
    <div className="privacy-policy-container">
      <div className="privacy-policy-content">
        <h1 className="main-title">BLINK DIGITAL PRIVACY POLICY FOR CONTEST PARTICIPANTS</h1>

        <p className="effective-date">
          <strong>Effective Date:</strong> 10th October 2025
        </p>

        <p className="intro-text">
          This Privacy Policy describes how Blink Digital Private Limited ("we," "us," or "our")
          collects, uses, stores, and shares your personal data when you participate in a contest,
          campaign, or activity ("Contest") hosted on our software-as-a-service (SaaS) platform
          ("Platform") on behalf of our client ("Client").
        </p>

        <p className="intro-text">
          For the purposes of the Digital Personal Data Protection Act, 2023 ("DPDPA"), and other
          applicable laws of India, the Client is the Data Principal, and Blink Digital acts as the
          Data Processor.
        </p>

        <section className="policy-section">
          <h2>INFORMATION WE COLLECT</h2>
          <p>
            When you participate in the Contest, we may collect the following categories of
            information on behalf of the Client:
          </p>

          <div className="subsection">
            <h3>Personal Data:</h3>
            <ul>
              <li>Name</li>
              <li>Email address</li>
              <li>Mobile number</li>
              <li>Age/date of birth (if applicable)</li>
              <li>Any other information provided during registration or participation</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Participation Data:</h3>
            <ul>
              <li>Quiz or game answers, scores, timestamps, and winner status</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Device and Technical Data:</h3>
            <ul>
              <li>IP address</li>
              <li>Device type, operating system, browser type</li>
              <li>Website or app usage data (via cookies and similar technologies)</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Location Data (Optional):</h3>
            <ul>
              <li>GPS or approximate location, only if you provide consent for location access</li>
            </ul>
          </div>
        </section>

        <section className="policy-section">
          <h2>PURPOSE OF PROCESSING</h2>
          <p>Your personal data is processed only for the following purposes:</p>
          <ul>
            <li>To facilitate your participation in the Contest</li>
            <li>To verify eligibility and determine winners</li>
            <li>To communicate with you (e.g., SMS, email, in-app messages) about the Contest</li>
            <li>To share winner details with the Client for prize approval and distribution</li>
            <li>To analyze campaign performance and improve future campaigns</li>
            <li>
              To send marketing or promotional communications only if you provide explicit consent
            </li>
            <li>To detect and prevent fraudulent activity or misuse of the Contest</li>
          </ul>
        </section>

        <section className="policy-section">
          <h2>LAWFUL BASIS FOR PROCESSING</h2>
          <p>Processing of your personal data is based on:</p>
          <ul>
            <li>
              Consent provided by you for participation and (if opted in) marketing communications
            </li>
            <li>
              Legitimate use necessary for the Client to conduct the Contest and fulfill prize
              distribution obligations
            </li>
          </ul>
          <p>
            You may withdraw your consent at any time by contacting us at{" "}
            <a href="mailto:compliance@blinkdigital.in">compliance@blinkdigital.in</a>
          </p>
        </section>

        <section className="policy-section">
          <h2>DATA SHARING AND DISCLOSURE</h2>
          <p>We may share your personal data in the following circumstances:</p>
          <ul>
            <li>
              <strong>With the Client (Data Principal):</strong> For winner verification, prize
              approval, and other Contest-related purposes.
            </li>
            <li>
              <strong>With Service Providers:</strong> Third-party vendors engaged by us to send
              SMS/email notifications or manage the Contest infrastructure.
            </li>
            <li>
              <strong>Legal Obligations:</strong> When required under applicable law or to respond
              to lawful requests by authorities.
            </li>
          </ul>
          <p>We do not sell or rent your personal data.</p>
        </section>

        <section className="policy-section">
          <h2>DATA RETENTION</h2>
          <p>
            Personal data will be retained only for as long as necessary to fulfill the purposes
            outlined in this Policy or as directed by the Client, unless a longer retention period
            is required by law. After that, your data will be securely deleted or anonymized.
          </p>
        </section>

        <section className="policy-section">
          <h2>DATA SECURITY</h2>
          <p>
            We implement reasonable technical and organizational measures to protect your data from
            unauthorized access, disclosure, alteration, or destruction. However, no system is
            completely secure, and participation is at your own risk.
          </p>
        </section>

        <section className="policy-section">
          <h2>YOUR RIGHTS UNDER DPDPA</h2>
          <p>
            Under the Digital Personal Data Protection Act, 2023, you have the following rights in
            relation to your personal data:
          </p>
          <ul>
            <li>
              <strong>Right to Access:</strong> Request details of personal data processed about
              you.
            </li>
            <li>
              <strong>Right to Correction and Erasure:</strong> Request correction or deletion of
              inaccurate or outdated personal data.
            </li>
            <li>
              <strong>Right to Grievance Redressal:</strong> Raise concerns regarding data handling.
            </li>
            <li>
              <strong>Right to Withdraw Consent:</strong> Withdraw consent at any time for
              processing or marketing communications.
            </li>
          </ul>
          <p>
            To exercise these rights, please contact us at{" "}
            <a href="mailto:compliance@blinkdigital.in">compliance@blinkdigital.in</a>. We will
            coordinate with the Client (Data Principal) to process your request.
          </p>
        </section>

        <section className="policy-section">
          <h2>COOKIES AND TRACKING</h2>
          <p>
            Our Platform may use cookies and similar technologies to enhance user experience and for
            analytics. You can manage cookie preferences through your browser settings.
          </p>
        </section>

        <section className="policy-section">
          <h2>CHILDREN'S DATA</h2>
          <p>
            Our Contests are not intended for individuals under 18 years of age unless explicitly
            stated and parental consent is obtained as required under law.
          </p>
        </section>

        <section className="policy-section">
          <h2>GRIEVANCE OFFICER</h2>
          <p>
            In compliance with the DPDPA, we have appointed a Grievance Officer to address data
            protection concerns:
          </p>
          <div className="contact-info">
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:Compliance@blinkdigital.in">Compliance@blinkdigital.in</a>
            </p>
            <p>
              <strong>Address:</strong> 102, Firdos Apartments, Waroda Road, Bandra West, Mumbai
              400050.
            </p>
          </div>
        </section>

        <section className="policy-section">
          <h2>CHANGES TO THIS POLICY</h2>
          <p>
            We may update this Privacy Policy periodically. The latest version will always be
            available on the Contest page or Platform.
          </p>
        </section>

        <div className="consent-notice">
          <div className="checkbox-wrapper">
            <input
              type="checkbox"
              id="privacy-agree"
              checked={isAgreed}
              onChange={handleCheckboxChange}
              className="privacy-checkbox"
            />
            <label htmlFor="privacy-agree" className="checkbox-label">
              I acknowledge that I have read and understood this Privacy Policy and consent to the
              processing of my personal data as described above.
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
